import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Badge,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  OverlayTrigger,
  Tooltip,
  DropdownButton,
  Dropdown,
  ButtonGroup
} from 'react-bootstrap';
import DesktopLayout from '../layouts/DesktopLayout';
import { AuthContext } from '../context/AuthContext';
import secureApiService from '../services/secureApi';
import axios from 'axios';
import { formatDate } from '../utils/dateFormatter';

const INTENT_LABELS = {
  guidebook_lookup: '📖 دليل',
  maintenance: '🔧 صيانة',
  housekeeping_supply: '🧹 تنظيف',
  early_check_in: '🚪 دخول مبكر',
  late_check_out: '🚪 خروج متأخر',
  task_status_query: '📋 استعلام',
  safety_alert: '🚨 سلامة'
};

const STATUS_COLORS = {
  active_ai: 'primary',
  active_ops: 'warning',
  open: 'warning',
  resolved: 'success'
};

const STATUS_LABELS = {
  active_ai: 'المساعد الذكي يرد الآن',
  active_ops: 'فريق العمليات يتابع الآن',
  open: '⏳ قيد الانتظار',
  resolved: 'محلول'
};

const HANDOFF_FILTER_OPTIONS = [
  { value: 'all', label: 'الكل' },
  { value: 'active_ai', label: 'المساعد الذكي' },
  { value: 'active_ops', label: 'فريق العمليات' }
];

const formatOpsAgentName = (conv, rosterMap) => {
  const fromRoster = rosterMap?.[conv?.assignedOpsAgent];
  const raw =
    fromRoster ||
    conv?.assignedOpsAgentName ||
    conv?.assignedOpsAgentDisplayName ||
    conv?.assignedOpsAgentEmail ||
    conv?.assignedOpsAgent;

  if (!raw) return null;

  const str = String(raw);
  const looksLikeObjectId = /^[a-fA-F0-9]{24}$/.test(str) || str.startsWith('ObjectId(');
  if (looksLikeObjectId) return null; // hide raw Mongo IDs from UI

  return str.length > 24 ? `${str.slice(0, 22)}...` : str;
};

const LiveConversationsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [error, setError] = useState(null);
  const [hoursFilter, setHoursFilter] = useState(48); // MVP: 48h default for natural auto-resolve
  const [selectedConv, setSelectedConv] = useState(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joining, setJoining] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [hoveredConv, setHoveredConv] = useState(null);
  const [reassigning, setReassigning] = useState(null); // Track which conversation is being reassigned
  const [handoffFilter, setHandoffFilter] = useState('all');
  const [opsRoster, setOpsRoster] = useState({});
  const chatEndRef = React.useRef(null);

  const TIME_FILTER_OPTIONS = [
    { value: 1, label: 'آخر ساعة' },
    { value: 6, label: 'آخر 6 ساعات' },
    { value: 24, label: 'آخر 24 ساعة' },
    { value: 48, label: 'آخر يومين (افتراضي)' },
    { value: 72, label: 'آخر 3 أيام' }
  ];

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'ops') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Poll for new messages while chat modal is open
  useEffect(() => {
    if (!hasJoined || !selectedConv) return;

    const pollInterval = setInterval(async () => {
      try {
        const details = await secureApiService.getConversationDetails(selectedConv.conversationId);
        const newMessages = details.messages || details.conversationHistory || [];

        // Only update if message count changed (avoid unnecessary re-renders)
        if (newMessages.length !== chatMessages.length) {
          setChatMessages(newMessages);
        }
      } catch (err) {
        console.error('Failed to poll messages', err);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [hasJoined, selectedConv, chatMessages.length]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoursFilter]);

  const activeOpsConversations = conversations.filter((conv) => conv.handoffStatus === 'active_ops');
  const filteredConversations = conversations.filter((conv) => {
    if (handoffFilter === 'active_ai') return conv.handoffStatus === 'active_ai';
    if (handoffFilter === 'active_ops') return conv.handoffStatus === 'active_ops';
    return true;
  });
  const hasFilteredConversations = filteredConversations.length > 0;

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { hours: hoursFilter };

      const response = await secureApiService.getLiveConversations(params);
      setConversations(response.conversations || []);
    } catch (err) {
      console.error('Failed to load live conversations', err);
      setError(err.response?.data?.message || 'فشل في تحميل المحادثات');
    } finally {
      setLoading(false);
    }
  };

  const fetchOpsRoster = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users`,
        { headers: { 'x-auth-token': token } }
      );
      const map = {};
      (response.data.users || []).forEach((u) => {
        map[u._id] = u.name || u.email || u.phone || u._id;
      });
      setOpsRoster(map);
    } catch (err) {
      console.error('Failed to fetch ops roster', err);
    }
  };

  useEffect(() => {
    // Fetch only ops/admin roster to resolve names for active_ops badges
    const loadRoster = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/users`,
          {
            headers: { 'x-auth-token': token },
            params: { role: 'ops,admin' }
          }
        );
        const map = {};
        (response.data.users || []).forEach((u) => {
          map[u._id] = u.name || u.email || u.phone || u._id;
        });
        setOpsRoster(map);
      } catch (err) {
        console.error('Failed to fetch ops roster', err);
      }
    };

    loadRoster();
  }, []);

  const handleJoinClick = async (conv) => {
    setSelectedConv(conv);
    setHasJoined(false);
    setMessageInput('');
    setChatMessages([]);

    // Load conversation messages if already joined
    if (conv.handoffStatus === 'active_ops') {
      try {
        const details = await secureApiService.getConversationDetails(conv.conversationId);
        setChatMessages(details.messages || details.conversationHistory || []);
        setHasJoined(true);
      } catch (err) {
        console.error('Failed to load conversation details', err);
      }
    }

    setShowJoinModal(true);
  };

  const handleSwitchConversation = (conv) => {
    if (!conv) return;
    handleJoinClick(conv);
  };

  const handleJoin = async () => {
    if (!selectedConv) return;

    try {
      setJoining(true);
      const response = await secureApiService.joinConversation(selectedConv.conversationId);
      setChatMessages(response.conversationHistory || []);
      setHasJoined(true);
      fetchConversations(); // Refresh list
    } catch (err) {
      console.error('Failed to join conversation', err);
      alert(err.response?.data?.message || 'فشل في الانضمام');
    } finally {
      setJoining(false);
    }
  };

  const handleSendMessage = async () => {
    const trimmed = messageInput.trim();

    // Validation
    if (!trimmed || !selectedConv || sending) return;

    if (trimmed.length > 2000) {
      alert('الرسالة طويلة جداً (الحد الأقصى 2000 حرف)');
      return;
    }

    try {
      setSending(true);
      const response = await secureApiService.sendOpsMessage(selectedConv.conversationId, trimmed);

      // Add message to chat immediately (optimistic update)
      const newMessage = {
        role: 'ops_agent',
        content: trimmed,
        timestamp: response.timestamp || new Date()
      };
      setChatMessages(prev => [...prev, newMessage]);
      setMessageInput('');
    } catch (err) {
      console.error('Failed to send message', err);
      alert(err.response?.data?.message || 'فشل في إرسال الرسالة');
    } finally {
      setSending(false);
    }
  };

  const handleCloseModal = () => {
    setShowJoinModal(false);
    setSelectedConv(null);
    setChatMessages([]);
    setMessageInput('');
    setHasJoined(false);
    fetchConversations(); // Refresh to update status
  };

  const handleResolve = async (conversationId) => {
    if (!window.confirm('هل أنت متأكد من إغلاق هذه المحادثة؟')) return;

    try {
      await secureApiService.resolveConversation(conversationId, 'تم الحل من لوحة العمليات');
      fetchConversations(); // Refresh
    } catch (err) {
      console.error('Failed to resolve conversation', err);
      alert(err.response?.data?.message || 'فشل في إغلاق المحادثة');
    }
  };

  const handleReassignToAI = async (conversationId) => {
    if (!window.confirm('هل تريد تسليم هذه المحادثة للمساعد الذكي؟ سيستأنف الرد تلقائياً عند رسالة الضيف التالية.')) return;

    try {
      setReassigning(conversationId); // Set loading state for this specific conversation
      await secureApiService.reassignConversation(conversationId, {
        assignTo: 'ai',
        reason: 'تسليم للمساعد الذكي من لوحة العمليات'
      });
      alert('✅ تم تسليم المحادثة للمساعد الذكي بنجاح');
      fetchConversations(); // Refresh

      // Close modal if currently viewing this conversation
      if (selectedConv?.conversationId === conversationId) {
        handleCloseModal();
      }
    } catch (err) {
      console.error('Failed to reassign conversation to AI', err);
      alert(err.response?.data?.message || 'فشل في تسليم المحادثة');
    } finally {
      setReassigning(null); // Clear loading state
    }
  };

  return (
    <DesktopLayout>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>المحادثات المباشرة</h2>
          <div className="d-flex gap-2">
            <Badge bg="info">{filteredConversations.length} محادثة معروضة</Badge>
            <Button variant="outline-secondary" size="sm" onClick={fetchConversations} disabled={loading}>
              🔄 تحديث
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <Card.Body className="d-flex flex-column gap-3">
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-2">
                <span role="img" aria-label="clock">⏱️</span>
                <strong>الفترة الزمنية</strong>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {TIME_FILTER_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    variant={hoursFilter === option.value ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setHoursFilter(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-2">
                <span role="img" aria-label="filter">⚙️</span>
                <strong>نوع المعالجة</strong>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {HANDOFF_FILTER_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    variant={handoffFilter === option.value ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setHandoffFilter(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading && conversations.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner animation="border" variant="primary" />
          </div>
        ) : !hasFilteredConversations ? (
          <Alert variant="info">لا توجد محادثات لهذا الفلتر حالياً</Alert>
        ) : (
          <Row>
            {filteredConversations.map((conv) => {
              return (
                <Col key={conv.conversationId} md={6} lg={4} className="mb-4">
                  <Card
                    className="h-100"
                    onMouseEnter={() => setHoveredConv(conv.conversationId)}
                    onMouseLeave={() => setHoveredConv(null)}
                    style={{ transition: 'box-shadow 0.2s', cursor: 'pointer' }}
                  >
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg={STATUS_COLORS[conv.handoffStatus] || 'secondary'}>
                          {STATUS_LABELS[conv.handoffStatus] || conv.handoffStatus}
                        </Badge>
                        {conv.handoffStatus === 'active_ops' && formatOpsAgentName(conv, opsRoster) && (
                          <Badge bg="light" text="dark" className="border text-nowrap">
                            <i className="fas fa-user me-1"></i>
                            {formatOpsAgentName(conv, opsRoster)}
                          </Badge>
                        )}
                      </div>
                      {conv.intent && (
                        <Badge bg="secondary">{INTENT_LABELS[conv.intent] || conv.intent}</Badge>
                      )}
                    </Card.Header>
                    <Card.Body>
                      <h6 className="mb-2">
                        <strong>{conv.propertyName || 'عقار غير معروف'}</strong>
                      </h6>
                      <p className="mb-1 text-muted small">
                        <strong>الضيف:</strong> {conv.guestName || conv.guestPhone}
                      </p>
                      <p className="mb-1 text-muted small">
                        <strong>بدأت:</strong> {formatDate(conv.startedAt)}
                      </p>
                      <p className="mb-2 text-muted small">
                        <strong>آخر رسالة:</strong> {formatDate(conv.lastMessageAt)}
                      </p>

                      {/* Hover Preview - Show full recent messages on hover */}
                      {hoveredConv === conv.conversationId && conv.recentMessages && conv.recentMessages.length > 0 ? (
                        <div className="mt-2 p-3 border rounded bg-white" style={{ maxHeight: '250px', overflowY: 'auto', fontSize: '0.9rem' }}>
                          <div className="mb-2 text-muted small">
                            <strong>معاينة المحادثة:</strong>
                          </div>
                          {conv.recentMessages.slice(0, 5).map((msg, idx) => (
                            <div key={idx} className="mb-2 pb-2 border-bottom">
                              <div>
                                <Badge bg={msg.role === 'user' ? 'primary' : msg.role === 'ops_agent' ? 'success' : 'dark'} className="mb-1">
                                  {msg.role === 'user' ? 'ضيف' : msg.role === 'ops_agent' ? 'عمليات' : 'المساعد الذكي'}
                                </Badge>
                              </div>
                              <div className="text-end" dir="rtl">{msg.content}</div>
                              <small className="text-muted">{formatDate(msg.timestamp)}</small>
                            </div>
                          ))}
                        </div>
                      ) : (
                        conv.recentMessages && conv.recentMessages.length > 0 && (
                          <div className="mt-2 p-2 bg-light rounded" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                            <small className="text-muted">مرر الماوس للمعاينة الكاملة</small>
                            <div className="mt-1">
                              <small>
                                {conv.recentMessages.slice(0, 2).map((msg, idx) => (
                                  <div key={idx} className="mb-1">
                                    <strong>{msg.role === 'user' ? 'ضيف:' : msg.role === 'ops_agent' ? 'عمليات:' : 'المساعد الذكي:'}</strong>{' '}
                                    {msg.content?.substring(0, 60)}...
                                  </div>
                                ))}
                              </small>
                            </div>
                          </div>
                        )
                      )}
                    </Card.Body>
                    <Card.Footer className="d-flex gap-2 flex-wrap">
                      {conv.handoffStatus === 'active_ops' && conv.assignedOpsAgent === user?.userId ? (
                        <>
                          <Button size="sm" variant="primary" onClick={() => handleJoinClick(conv)}>
                            <i className="fas fa-folder-open me-1"></i>
                            فتح المحادثة
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleReassignToAI(conv.conversationId)}
                            disabled={reassigning === conv.conversationId}
                            title="تسليم المحادثة للمساعد الذكي - سيستأنف الرد عند رسالة الضيف التالية"
                          >
                            {reassigning === conv.conversationId ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                جاري التسليم...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-robot me-1"></i>
                                تسليم للمساعد الذكي
                              </>
                            )}
                          </Button>
                          <Button size="sm" variant="success" onClick={() => handleResolve(conv.conversationId)}>
                            <i className="fas fa-check me-1"></i>
                            إغلاق
                          </Button>
                        </>
                      ) : conv.canJoin ? (
                        <Button size="sm" variant="outline-primary" onClick={() => handleJoinClick(conv)}>
                          <i className="fas fa-sign-in-alt me-1"></i>
                          انضم للمحادثة
                        </Button>
                      ) : null}
                    </Card.Footer>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Join/Chat Modal */}
        <Modal show={showJoinModal} onHide={handleCloseModal} dir="rtl" size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {hasJoined ? 'محادثة مع الضيف' : 'الانضمام إلى المحادثة'}
            </Modal.Title>
            {activeOpsConversations.length > 0 && (
              <DropdownButton
                as={ButtonGroup}
                variant="outline-secondary"
                size="sm"
                title={`${selectedConv?.propertyName || 'محادثاتك'} · ${selectedConv?.guestName || selectedConv?.guestPhone || ''}`}
                className="me-2"
              >
                {activeOpsConversations.map((conv, idx) => (
                  <React.Fragment key={conv.conversationId}>
                    <Dropdown.Item
                      active={selectedConv?.conversationId === conv.conversationId}
                      onClick={() => handleSwitchConversation(conv)}
                    >
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <span className="text-truncate" style={{ maxWidth: '160px' }}>
                          {conv.propertyName || 'عقار'}
                        </span>
                        <Badge bg={STATUS_COLORS[conv.handoffStatus]}>نشط</Badge>
                      </div>
                      <div className="small text-muted text-truncate" style={{ maxWidth: '200px' }}>
                        {conv.guestName || conv.guestPhone}
                      </div>
                    </Dropdown.Item>
                    {idx < activeOpsConversations.length - 1 && <Dropdown.Divider />}
                  </React.Fragment>
                ))}
              </DropdownButton>
            )}
          </Modal.Header>
          <Modal.Body>
            {selectedConv && (
              <>
                <div className="d-flex flex-wrap gap-3 align-items-start mb-3">
                  <Badge bg="light" text="dark" className="px-2 py-1">
                    العقار: {selectedConv.propertyName || 'غير متوفر'}
                  </Badge>
                  <Badge bg="light" text="dark" className="px-2 py-1">
                    الضيف: {selectedConv.guestName || selectedConv.guestPhone}
                  </Badge>
                  <Badge bg="light" text="dark" className="px-2 py-1">
                    بدأت: {formatDate(selectedConv.startedAt)}
                  </Badge>
                  <Badge bg="light" text="dark" className="px-2 py-1">
                    آخر رسالة: {formatDate(selectedConv.lastMessageAt)}
                  </Badge>
                </div>

                {!hasJoined && (
                  <Alert variant="warning">
                    عند الانضمام، سيتوقف المساعد الذكي عن الرد وستتولى المحادثة.
                  </Alert>
                )}

                {hasJoined && (
                  <>
                    {/* Chat Messages */}
                    <div
                      className="border rounded p-3 mb-3"
                      style={{
                        maxHeight: '620px',
                        overflowY: 'auto',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      {chatMessages.length === 0 ? (
                        <p className="text-muted text-center">لا توجد رسائل</p>
                      ) : (
                        <>
                          {chatMessages.map((msg, idx) => {
                            const prevMsg = chatMessages[idx - 1];
                            const currentTime = formatDate(msg.timestamp);
                            const prevTime = prevMsg ? formatDate(prevMsg.timestamp) : null;
                            const showTimeChip = currentTime !== prevTime;

                            return (
                              <React.Fragment key={idx}>
                                {showTimeChip && (
                                  <div className="text-center text-muted small mb-2">
                                    {currentTime}
                                  </div>
                                )}
                                  <div
                                    className={`mb-2 p-2 rounded d-flex flex-column ${
                                      msg.role === 'user' ? 'bg-white text-end align-items-end' :
                                      msg.role === 'ops_agent' ? 'bg-success text-white align-items-start' :
                                      'bg-info text-white align-items-start'
                                    }`}
                                    style={{ gap: '4px', boxShadow: '0 0 0 1px rgba(0,0,0,0.05)' }}
                                  >
                                  <Badge bg={msg.role === 'user' ? 'secondary' : msg.role === 'ops_agent' ? 'dark' : 'dark'}>
                                    {msg.role === 'user' ? 'ضيف' :
                                     msg.role === 'ops_agent' ? 'مشرف' :
                                     'المساعد الذكي'}
                                  </Badge>
                                  <div className="w-100" style={{ lineHeight: 1.5, fontSize: '15px' }}>{msg.content}</div>
                                </div>
                              </React.Fragment>
                            );
                          })}
                          <div ref={chatEndRef} />
                        </>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="d-flex gap-2 align-items-end">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="اكتب رسالتك هنا..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={sending}
                        dir="rtl"
                      />
                      <Button
                        variant="primary"
                        onClick={handleSendMessage}
                        disabled={sending || !messageInput.trim()}
                      >
                        {sending ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="ms-2"
                            />
                            جارٍ الإرسال
                          </>
                        ) : 'إرسال'}
                      </Button>
                    </div>
                    <div className="text-muted small mt-2">Enter للإرسال · Shift+Enter لسطر جديد</div>
                  </>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="justify-content-start gap-2">
            {!hasJoined ? (
              <>
                <Button variant="primary" onClick={handleJoin} disabled={joining}>
                  {joining ? 'جارٍ الانضمام...' : 'تأكيد الانضمام'}
                </Button>
                <Button variant="secondary" onClick={handleCloseModal} disabled={joining}>
                  إلغاء
                </Button>
              </>
            ) : (
              <>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="resolve-tip">يُنهي المحادثة ويخفيها من القائمة</Tooltip>}
                >
                  <span>
                    <Button
                      variant="success"
                      onClick={() => {
                        handleResolve(selectedConv.conversationId);
                        handleCloseModal();
                      }}
                    >
                      وضع علامة كمحلول
                    </Button>
                  </span>
                </OverlayTrigger>
                <Button variant="secondary" onClick={handleCloseModal}>
                  إغلاق
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </DesktopLayout>
  );
};

export default LiveConversationsPage;
